import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import classNames from 'classnames'

import { FormatsManager } from 'react-wot-bootstrap'

import { toRoman } from 'helpers'
import { CHALLENGES, FORT_BATTLES, SORTIES_WITH_LEVEL } from 'helpers/i18n'
import { isTodayDay } from 'helpers/datetime'
import {
    checkScheduleType,
    checkIsFirstPeriod,
    checkTimeInsidePlayday,
    isScheduleDisabledOrOverridden,
    vehicleLevelFilter,
} from 'helpers/schedule'
import { UNIT_TYPES } from 'constants'
import { HAS_OFFSET, SCHEDULE_TYPES } from '../../constants'

import NoBattlesMessage from '../NoBattlesMessage/NoBattlesMessage'
import BattleModeAtPeripheryOverriddenTooltip from './tooltips/BattleModeAtPeripheryOverriddenTooltip'
import TimeLine from '../TimeLine/TimeLine'

import styles from './ScheduleTable.scss'

export class ScheduleTable extends React.Component {
    static propTypes = {
        playday: PropTypes.object.isRequired,
        unitType: PropTypes.oneOf(Object.values(UNIT_TYPES)),
        vehicleLevel: PropTypes.number,
        type: PropTypes.oneOf(Object.values(SCHEDULE_TYPES)),

        playdayStart: PropTypes.number.isRequired,
        playdayEnd: PropTypes.number.isRequired,
        playdays: PropTypes.array.isRequired,
        selectedPlaydayId: PropTypes.number.isRequired,
    }

    static defaultProps = { type: SCHEDULE_TYPES.MAIN_SCHEDULE }

    state = {
        time: moment(),
    }

    constructor() {
        super()

        const formats = FormatsManager.getFormats()
        this.dateFormat = formats.shortDate

        this.tableHeadTimeFormat = moment.localeData()._longDateFormat.LT
        if (this.tableHeadTimeFormat.endsWith('A')) {
            // https://jira.wargaming.net/browse/WGSH-3397
            this.tableHeadTimeFormat = this.tableHeadTimeFormat.replace(':mm', '')
        }

        this.getTableHeadTimeCell = this.getTableHeadTimeCell.bind(this)
    }

    componentDidMount() {
        this.interval = setInterval(this.tick, 1000)
    }

    componentWillUnmount() {
        clearInterval(this.interval)
    }

    tick = () => {
        this.setState({ time: moment() })
    }

    getPlaydayLength = () => {
        let end = this.props.playdayEnd
        if (end < this.props.playdayStart) end += 24
        return end - this.props.playdayStart
    }

    /**
     * we need to have padding of 1 hour before and after the schedule
     * @example
     * this.props.playdayStart = 10
     * this.props.playdayEnd = 11
     * this.getPlayingHours() === [9, 10, 11, 12]
     * @example
     * this.props.playdayStart = 23
     * this.props.playdayEnd = 1
     * this.getPlayingHours() === [22, 23, 0, 1, 2]
     * @returns {int[]} array of hours to render over the schedule grid
     */
    getPlayingHours = () => {
        let firstPlayingHour = this.props.playdayStart - 1
        if (firstPlayingHour === -1) firstPlayingHour = 23
        return Array.from({ length: this.getPlaydayLength() + 3 }, (_, index) => {
            return (firstPlayingHour + index) % 24
        })
    }

    getTableHead() {
        let playdayDate = moment()
            .add(this.props.selectedPlaydayId, 'day')
            .format(this.dateFormat)
        return (
            <div className={styles.tableHead}>
                <div className={styles.td_name}>
                    {playdayDate}
                </div>
                <div className={styles.td_wrapper}>
                    {this.getPlayingHours().map(this.getTableHeadTimeCell)}
                </div>
            </div>
        )
    }

    getTableHeadTimeCell(utcHour, index) {
        return (
            <div className={styles.td_time} key={`td_time_${utcHour}_${index}`}>
                {getLocalTimeFromUTCHour(utcHour).format(this.tableHeadTimeFormat)}
            </div>
        )
    }

    /**
     * @param {string} schedule_type
     * @param {string} vehicle_level
     * @param {Object[]} schedules of peripheries for current battle mode
     * @param {int} hour of the cell in utc
     * @param {string[]} peripheriesOrder positions of bars to calculate top offset of the bar
     * @returns {React.Component} schedule table cell with gantt bars of schedules that start at {hour}
     */
    getTableCell = (schedule_type, vehicle_level, schedules, hour, peripheriesOrder, index) => {

        const getBarsForCell = (schedule, barIndex) => {
            const period = schedule.periods.find(period => utcHourToLocal(period[0]) === hour)
            if (!period) return null

            let [start, end] = period
            if (end < start) end += 24

            const peripheryPosition = peripheriesOrder.indexOf(schedule.periphery)
            const top = (peripheryPosition + 1) / (peripheriesOrder.length + 1) * 100 + '%'
            const width = (end - start) * 100 + '%' // width is set in cell sizes

            const style = {
                width: `calc(${width} - 4px)`,
                top,
                left: '1px',
            }

            const isFirstPeriod = checkIsFirstPeriod(
                period, schedule.periods, this.props.playdayStart, this.props.playdayEnd
            )

            const tooltipId = this.getPeripheryTooltipId(schedule_type, vehicle_level, schedule.periphery)
            let caption
            if (schedule.periphery && isFirstPeriod) { // draw caption only for first bar per periphery
                caption = (
                    <div className={styles.barName}>
                        <div className={styles.warningIcon} data-tip data-for={tooltipId} />
                        {schedule.periphery}
                    </div>
                )
            } else if (schedule_type === 'CHALLENGE' && isFirstPeriod) {
                caption = (
                    <div className={classNames(styles.barName, styles.barName__challenge)} />
                )
            }
            const className = classNames(
                styles.bar,
                {
                    [styles[`bar__${schedule_type.toLowerCase()}`]]: schedule.enabled !== false,
                    [styles.bar__disabled]: schedule.enabled === false,
                    [styles.bar__overridden]: schedule.overridden === true,
                }
            )

            return (
                <div
                    className={className}
                    style={style}
                    key={barIndex}
                >
                    {caption}
                </div>
            )
        }

        const bars = schedules.map(getBarsForCell)
        return (
            <div className={styles.td} key={hour + '_' + index}>
                <div className={classNames(styles.td_inner)} />
                {bars}
            </div>
        )
    }

    getPeripheryTooltipId = (type, vehicle_level, periphery) => (
        `calendar.selected_day.${type}${vehicle_level}.${periphery}.tooltip`
    )

    getTableRow = (scheduleItem, index) => {

        const { time } = this.state
        let name
        switch (scheduleItem.type) {
        case 'SORTIE':
            name = SORTIES_WITH_LEVEL(toRoman(scheduleItem.vehicle_level))
            break
        case 'FORT_BATTLE':
            name = FORT_BATTLES
            break
        case 'CHALLENGE':
            name = CHALLENGES
            break
        }

        let schedules = scheduleItem.schedules || []

        const peripheriesOrder = schedules
            .filter(schedule => schedule.periods.length > 0)
            .sort(sortSchedulesByPrimetimeStart)
            .map(schedule => schedule.periphery)

        const cellsCount = this.getPlaydayLength() + 2 // 2 cells of padding: left and right
        const cells = Array.from(
            { length: cellsCount },
            (_, index) => {
                const hoursSinceTableStart = this.props.playdayStart + index - 1
                const localHour = moment(time).utc().hour(hoursSinceTableStart).local().hour()

                return this.getTableCell(
                    scheduleItem.type, scheduleItem.vehicle_level, schedules,
                    localHour, peripheriesOrder, index
                )
            }
        )

        const peripheryOverriddenTooltips = schedules
            .filter(isScheduleDisabledOrOverridden)
            .map(schedule => (
                <BattleModeAtPeripheryOverriddenTooltip
                    id={this.getPeripheryTooltipId(
                        scheduleItem.type, scheduleItem.vehicle_level, schedule.periphery
                    )}
                    isDisabled={!schedule.enabled}
                    peripheryName={schedule.periphery}
                    vehicleLevel={scheduleItem.vehicle_level}
                    battleMode={scheduleItem.type}
                    key={schedule.periphery}
                />
            ))

        return (
            <div className={styles.tr} key={index}>
                <div className={styles.td_name}>
                    {name}
                </div>
                <div className={styles.td_wrapper}>
                    {cells}
                </div>
                {peripheryOverriddenTooltips}
            </div>
        )
    }

    getTableBody() {
        const scheduleItems = this.props.playday.schedule_items.filter(
            scheduleItem => checkScheduleType(scheduleItem, this.props.unitType),
        )

        const sorties = scheduleItems.filter(scheduleItem => scheduleItem.type === 'SORTIE')
        const fortBattles = scheduleItems.filter(scheduleItem => scheduleItem.type === 'FORT_BATTLE')
        const challenges = scheduleItems.filter(scheduleItem => scheduleItem.type === 'CHALLENGE')

        const tableSectionsItems = [
            sorties,
            fortBattles,
            challenges,
        ]

        const getTableSection = (scheduleItems, sectionIndex) => {
            if (!scheduleItems.length) return null
            const style = {
                flexGrow: scheduleItems.length,
            }
            const date = moment(this.props.playday.date).get('date')

            const rows = scheduleItems
                .filter(scheduleItem => vehicleLevelFilter(scheduleItem, this.props.vehicleLevel))
                .map((scheduleItem, itemIndex) => this.getTableRow(scheduleItem, itemIndex))

            const sectionClassNames = classNames(
                styles.tableSection,
                (this.props.type === SCHEDULE_TYPES.MAIN_SCHEDULE) && styles.tableSection__mainSchedule,
            )

            return (
                <div
                    className={sectionClassNames}
                    key={`${date}_tableSection_${sectionIndex}`}
                    style={style}
                >
                    {rows}
                </div>
            )
        }

        return tableSectionsItems.map(getTableSection)
    }

    render() {
        const scaleStart = this.props.playdayStart - 1
        const isInsideExpandedPlayday = checkTimeInsidePlayday(
            this.state.time,
            this.props.playday.date,
            scaleStart,
            this.props.playdayEnd,
        )
        const isBattlesToday = this.props.playday.schedule_items
            .filter(item => vehicleLevelFilter(item, this.props.vehicleLevel))
            .some(item => checkScheduleType(item, this.props.unitType)
        )

        let timeLine
        if (isInsideExpandedPlayday && isBattlesToday)
            timeLine = (
                <TimeLine
                    forOneBattleMode={this.props.type === 'oneBattleMode'}
                    forAllBattleModes={this.props.type === 'allBattleModes'}
                    scaleStartHour={scaleStart}
                    scaleLengthHour={this.getPlaydayLength() + 2}
                    now={this.state.time}
                />
            )

        let tableBody
        if (!isBattlesToday) {
            tableBody = (
                <div className={styles.noBattles}>
                    <NoBattlesMessage
                        showDescription={isTodayDay(moment(this.props.playday.date))}
                        playdays={this.props.playdays}
                        playdayStart={this.props.playdayStart}
                        playdayEnd={this.props.playdayEnd}
                        unitType={this.props.unitType}
                    />
                </div>
            )
        } else {
            tableBody = this.getTableBody()
        }

        const baseClassNames = classNames(styles.base, {
            [styles.base__with_offset]: HAS_OFFSET,
            [styles.forOneBattleMode]: this.props.type === 'oneBattleMode',
            [styles.forAllBattleModes]: this.props.type === 'allBattleModes',
        })

        return (
            <div className={baseClassNames}>
                {this.getTableHead()}
                <div className={styles.tableBody}>
                    {tableBody}
                </div>
                {timeLine}
            </div>
        )
    }
}

export const mapStateToProps = ({ schedule }) => ({
    playdayStart: schedule.playday_start,
    playdayEnd: schedule.playday_end,
    playdays: schedule.playdays,
    selectedPlaydayId: schedule.selected_playday_id,
})

export default connect(mapStateToProps)(ScheduleTable)

function sortSchedulesByPrimetimeStart(a, b) {
    return a.periods[0][0] - b.periods[0][0]
}

function getLocalTimeFromUTCHour(hour) {
    return moment.utc().hours(hour).startOf('hour').local()
}

function utcHourToLocal(hour) {
    return moment.utc().hours(hour).local().hour()
}



// WEBPACK FOOTER //
// ./containers/Schedule/components/ScheduleTable/ScheduleTable.jsx